import { useEffect, useRef, useState } from 'react';

import { useAuthStore } from '@/features/auth/auth-store';

import { type Referral, type ReferralAssignee, useReferralsStore } from './referrals-store';
import { useTeammates } from './useTeammates';

/** Staged choice inside the modal: a teammate pick OR the release card. */
type StagedPick = { kind: 'teammate'; who: ReferralAssignee } | { kind: 'release' } | null;

export interface ReassignModalProps {
  /** The referrals being reassigned (1 = single mode; >1 = batch mode). */
  referrals: Referral[];
  onClose: () => void;
  /** Toast dispatcher (title/body object form). */
  notify: (title: string, body: string) => void;
}

/**
 * The Reassign / Release picker (SCRUM-332) — v6 `renderAssignModal`:
 * settings-modal chrome, the Release-to-Branch-Manager card, an "or assign to
 * a teammate" divider, a search field that re-renders only the list (focus
 * preserved), staged selection (hover reveals "Select"; the current assignee
 * shows CURRENT; the picked row shows a gold check), an optional note, and a
 * confirm CTA whose label reflects the staged choice.
 */
export function ReassignModal({ referrals, onClose, notify }: ReassignModalProps) {
  const [staged, setStaged] = useState<StagedPick>(null);
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const roster = useTeammates();
  const reassign = useReferralsStore((s) => s.reassign);
  const release = useReferralsStore((s) => s.release);
  const user = useAuthStore((s) => s.user);

  const batch = referrals.length > 1;
  const single = referrals[0];
  const currentAssignee = !batch ? single?.assignee : null;

  useEffect(() => {
    const t = setTimeout(() => searchRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const results = roster.filter((t) => {
    if (t.email.toLowerCase() === user?.email.toLowerCase()) return false;
    if (!q) return true;
    return `${t.name} ${t.role}`.toLowerCase().includes(q);
  });

  const confirm = () => {
    if (!staged) return;
    const by = user?.displayName ?? 'You';
    for (const r of referrals) {
      if (staged.kind === 'release') {
        release(r.id, by);
      } else {
        reassign(r.id, staged.who, by, note.trim() || undefined);
      }
    }
    if (staged.kind === 'release') {
      notify(
        batch ? 'Referrals released' : 'Returned to the Branch Manager',
        batch
          ? `${String(referrals.length)} referrals released to the Branch Manager queue.`
          : 'Released to the Branch Manager queue. The submitter was notified.',
      );
    } else {
      notify(
        batch ? 'Referrals reassigned' : 'Referral reassigned',
        batch
          ? `${String(referrals.length)} referrals assigned to ${staged.who.name}.`
          : `${staged.who.name} will follow up${note.trim() ? ' (note added)' : ''}. The submitter was notified.`,
      );
    }
    onClose();
  };

  const ctaLabel = !staged
    ? 'Reassign'
    : staged.kind === 'release'
      ? 'Release to Branch Manager'
      : `Reassign to ${staged.who.name}`;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
      <div
        role="dialog"
        aria-label={batch ? `Reassign ${String(referrals.length)} referrals` : 'Reassign referral'}
        className="bg-bg-4 border-border-1 rounded-tk-lg shadow-tk-lg flex max-w-[460px] min-w-[420px] flex-col border"
      >
        <div className="border-border-0 border-b px-5 py-4">
          <div className="font-display text-text-0 text-base font-semibold">
            {batch ? `Reassign ${String(referrals.length)} referrals` : 'Reassign referral'}
          </div>
          {!batch && single && (
            <div className="text-text-2 mt-1 text-[0.8125rem]">{single.customerName}</div>
          )}
        </div>

        {/* Release card */}
        <button
          type="button"
          onClick={() => {
            setStaged(staged?.kind === 'release' ? null : { kind: 'release' });
          }}
          className={`rounded-tk-md mx-4 mt-3 flex items-center gap-[11px] border px-3 py-2.5 text-left transition-colors ${
            staged?.kind === 'release'
              ? 'border-[color-mix(in_srgb,var(--cyan)_40%,var(--border-1))] [background:color-mix(in_srgb,var(--cyan)_12%,transparent)]'
              : 'bg-bg-2 border-border-1 hover:bg-bg-3 hover:border-border-2'
          }`}
        >
          <span className="text-text-2 bg-bg-4 border-border-1 grid h-7 w-7 flex-none place-items-center rounded-full border">
            <svg
              viewBox="0 0 15 15"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 7.5a5.5 5.5 0 1 1 1.6 3.9" />
              <polyline points="2 8 2 11.5 5.5 11.5" />
            </svg>
          </span>
          <span className="flex flex-col leading-[1.3]">
            <span className="text-text-0 text-sm font-semibold">
              {batch ? 'Release referrals' : 'Release referral'}
            </span>
            <span className="text-text-3 text-xs">Send back to the Branch Manager queue</span>
          </span>
          {staged?.kind === 'release' && (
            <span className="bg-brand-primary ml-auto grid h-5 w-5 flex-none place-items-center rounded-full text-white">
              <CheckIcon />
            </span>
          )}
        </button>

        {/* Divider */}
        <div className="text-text-3 mx-4 my-2 flex items-center gap-2 font-mono text-[0.5625rem] font-bold tracking-[0.08em] uppercase before:h-px before:flex-1 before:bg-[var(--border-0)] after:h-px after:flex-1 after:bg-[var(--border-0)]">
          or assign to a teammate
        </div>

        {/* Search */}
        <div className="border-border-1 rounded-tk-md text-text-3 mx-4 mb-2 flex items-center gap-2 border px-2.5 py-[7px]">
          <svg
            viewBox="0 0 14 14"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="6" cy="6" r="4.2" />
            <path d="M12.5 12.5L9.2 9.2" />
          </svg>
          <input
            ref={searchRef}
            value={query}
            placeholder="Search teammates…"
            aria-label="Search teammates"
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            className="text-text-0 placeholder:text-text-3 min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
          />
        </div>

        {/* List */}
        <div className="relative">
          <div className="max-h-[320px] overflow-y-auto px-2 pb-2">
            {results.length === 0 ? (
              <p className="text-text-3 p-6 text-center text-[0.8125rem]">
                No teammates match “{query.trim()}”.
              </p>
            ) : (
              results.map((t) => {
                const isCurrent = currentAssignee?.email === t.email;
                const isPicked = staged?.kind === 'teammate' && staged.who.email === t.email;
                return (
                  <button
                    key={t.email}
                    type="button"
                    onClick={() => {
                      setStaged(isPicked ? null : { kind: 'teammate', who: t });
                    }}
                    className={`group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                      isPicked
                        ? '[background:color-mix(in_srgb,var(--cyan)_12%,transparent)] hover:[background:color-mix(in_srgb,var(--cyan)_16%,transparent)]'
                        : isCurrent
                          ? '[background:color-mix(in_srgb,var(--cyan)_8%,transparent)]'
                          : 'hover:bg-bg-3'
                    }`}
                  >
                    <span className="text-brand-primary grid h-[30px] w-[30px] flex-none place-items-center rounded-full font-mono text-[0.6875rem] font-bold [background:color-mix(in_srgb,var(--cyan)_14%,transparent)]">
                      {initialsOf(t.name)}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col leading-[1.25]">
                      <span className="text-text-0 text-sm">{t.name}</span>
                      <span className="text-text-3 truncate text-xs">{t.role}</span>
                    </span>
                    {isPicked ? (
                      <span className="bg-brand-primary grid h-5 w-5 flex-none place-items-center rounded-full text-white">
                        <CheckIcon />
                      </span>
                    ) : isCurrent ? (
                      <span className="text-text-3 text-[0.6875rem] font-bold tracking-[0.06em] uppercase">
                        Current
                      </span>
                    ) : (
                      <span className="text-brand-primary text-xs font-semibold opacity-0 transition-opacity group-hover:opacity-100">
                        Select
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-[22px] bg-gradient-to-b from-transparent to-[var(--bg-4)]" />
        </div>

        {/* Optional note */}
        <div className="mx-4 mt-2 flex flex-col gap-[5px]">
          <span className="text-text-2 text-xs font-medium">
            Add a note <span className="text-text-3 font-normal">(optional)</span>
          </span>
          <textarea
            value={note}
            rows={2}
            aria-label="Add a note"
            placeholder="Context for whoever takes this on…"
            onChange={(e) => {
              setNote(e.target.value);
            }}
            className="bg-bg-3 border-border-1 rounded-tk-md text-text-0 placeholder:text-text-3 min-h-[46px] w-full resize-y border px-2.5 py-2 text-[0.8125rem] outline-none focus:border-[color-mix(in_srgb,var(--cyan)_45%,var(--border-1))] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--cyan)_9%,transparent)]"
          />
        </div>

        {/* Actions */}
        <div className="border-border-0 mt-3 flex justify-end gap-2 border-t px-5 pt-3 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-bg-3 border-border-1 rounded-tk-sm text-text-1 hover:bg-bg-2 hover:text-text-0 border px-3 py-[5px] text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!staged}
            onClick={confirm}
            className="bg-brand-primary rounded-tk-md px-3.5 py-[7px] text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 12 12"
      width="9"
      height="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="2 6.5 5 9.5 10 3" />
    </svg>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase();
}
